import React, { useState, useEffect } from 'react';
import {
  Upload,
  FileText,
  Download,
  Eye,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Shield,
  FileCheck,
  X,
  RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Vendor } from '../types/vendor';
import { useTheme } from '../hooks/useTheme';

interface VendorDocumentManagerProps {
  vendor: Vendor;
  user: {
    id: string;
    role: string;
  };
  onUpdate?: () => void;
}

type DocumentType = 
  | 'general_liability' 
  | 'workers_comp' 
  | 'license' 
  | 'w9' 
  | 'certificate' 
  | 'other';

interface VendorDocument {
  id: string;
  type: DocumentType;
  name: string;
  category: string;
  fileUrl: string;
  fileSize: number; // bytes
  mimeType: string;
  uploadedAt: Date;
  uploadedBy: {
    id: string;
    name: string;
  };
  expirationDate?: Date;
  policyNumber?: string;
  provider?: string;
  notes?: string;
  status: 'active' | 'expired' | 'expiring_soon';
  version: number;
  previousVersionId?: string;
}

export default function VendorDocumentManager({
  vendor,
  user,
  onUpdate,
}: VendorDocumentManagerProps) {
  const { isDarkMode } = useTheme();
  const [documents, setDocuments] = useState<VendorDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VendorDocument | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'expiring' | 'expired'>('all');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    type: 'general_liability' as DocumentType,
    name: '',
    file: null as File | null,
    expirationDate: '',
    policyNumber: '',
    provider: '',
    notes: '',
  });

  useEffect(() => {
    loadDocuments();
  }, [vendor.id]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);
      
      // In production, fetch from Firebase Storage + Firestore
      // For now, generate from vendor data
      const docs: VendorDocument[] = [];
      
      // General Liability Insurance
      if (vendor.generalLiability) {
        docs.push({
          id: `gl-${vendor.id}`,
          type: 'general_liability',
          name: 'General Liability Insurance',
          category: 'Insurance',
          fileUrl: vendor.generalLiability.certificateUrl || '',
          fileSize: 245632,
          mimeType: 'application/pdf',
          uploadedAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
          uploadedBy: {
            id: vendor.createdBy,
            name: vendor.createdByName,
          },
          expirationDate: new Date(vendor.generalLiability.expirationDate),
          policyNumber: vendor.generalLiability.policyNumber,
          provider: vendor.generalLiability.provider,
          status: getDocumentStatus(new Date(vendor.generalLiability.expirationDate)),
          version: 1,
        });
      }
      
      // Workers Compensation
      if (vendor.workersCompensation) {
        docs.push({
          id: `wc-${vendor.id}`,
          type: 'workers_comp',
          name: 'Workers Compensation Insurance',
          category: 'Insurance',
          fileUrl: vendor.workersCompensation.certificateUrl || '',
          fileSize: 189420,
          mimeType: 'application/pdf',
          uploadedAt: new Date(Date.now() - 75 * 24 * 60 * 60 * 1000),
          uploadedBy: {
            id: vendor.createdBy,
            name: vendor.createdByName,
          },
          expirationDate: new Date(vendor.workersCompensation.expirationDate),
          policyNumber: vendor.workersCompensation.policyNumber,
          provider: vendor.workersCompensation.provider,
          status: getDocumentStatus(new Date(vendor.workersCompensation.expirationDate)),
          version: 1,
        });
      }
      
      // Licenses
      vendor.licenses.forEach((license, idx) => {
        docs.push({
          id: `lic-${vendor.id}-${idx}`,
          type: 'license',
          name: `${license.type} License`,
          category: 'License',
          fileUrl: license.fileUrl || '',
          fileSize: 156789,
          mimeType: 'application/pdf',
          uploadedAt: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000),
          uploadedBy: {
            id: vendor.createdBy,
            name: vendor.createdByName,
          },
          expirationDate: new Date(license.expirationDate),
          policyNumber: license.number,
          notes: `${license.state} - ${license.type}`,
          status: getDocumentStatus(new Date(license.expirationDate)),
          version: 1,
        });
      });
      
      // W9 Form
      if (vendor.w9Uploaded && vendor.w9FileUrl) {
        docs.push({
          id: `w9-${vendor.id}`,
          type: 'w9',
          name: 'W-9 Tax Form',
          category: 'Tax',
          fileUrl: vendor.w9FileUrl,
          fileSize: 98765,
          mimeType: 'application/pdf',
          uploadedAt: new Date(Date.now() - 150 * 24 * 60 * 60 * 1000),
          uploadedBy: {
            id: vendor.createdBy,
            name: vendor.createdByName,
          },
          status: 'active',
          version: 1,
        });
      }
      
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentStatus = (expirationDate: Date): 'active' | 'expired' | 'expiring_soon' => {
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiration < 0) return 'expired';
    if (daysUntilExpiration <= 30) return 'expiring_soon';
    return 'active';
  };

  const getDaysUntilExpiration = (expirationDate?: Date): number => {
    if (!expirationDate) return Infinity;
    const now = new Date();
    return Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (status: 'active' | 'expired' | 'expiring_soon') => {
    const badges = {
      active: <Badge className="bg-green-100 text-green-800">Active</Badge>,
      expiring_soon: <Badge className="bg-yellow-100 text-yellow-800">Expiring Soon</Badge>,
      expired: <Badge className="bg-red-100 text-red-800">Expired</Badge>,
    };
    return badges[status];
  };

  const getStatusIcon = (status: 'active' | 'expired' | 'expiring_soon') => {
    const icons = {
      active: <CheckCircle className="w-5 h-5 text-green-600" />,
      expiring_soon: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
      expired: <X className="w-5 h-5 text-red-600" />,
    };
    return icons[status];
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type (PDF, images, etc.)
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload PDF or image files only.');
        return;
      }
      
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('File too large. Maximum size is 10MB.');
        return;
      }
      
      setUploadForm({ ...uploadForm, file, name: file.name });
    }
  };

  const handleUploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }
    
    try {
      setIsUploading(true);
      setUploadProgress(0);
      
      // In production, this would:
      // 1. Upload to Firebase Storage
      // 2. Create Firestore document
      // 3. Update vendor record
      
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        setUploadProgress(i);
      }
      
      // Mock new document
      const newDoc: VendorDocument = {
        id: `doc-${Date.now()}`,
        type: uploadForm.type,
        name: uploadForm.name || uploadForm.file.name,
        category: getCategoryFromType(uploadForm.type),
        fileUrl: URL.createObjectURL(uploadForm.file),
        fileSize: uploadForm.file.size,
        mimeType: uploadForm.file.type,
        uploadedAt: new Date(),
        uploadedBy: {
          id: user.id,
          name: user.role,
        },
        expirationDate: uploadForm.expirationDate ? new Date(uploadForm.expirationDate) : undefined,
        policyNumber: uploadForm.policyNumber || undefined,
        provider: uploadForm.provider || undefined,
        notes: uploadForm.notes || undefined,
        status: uploadForm.expirationDate 
          ? getDocumentStatus(new Date(uploadForm.expirationDate))
          : 'active',
        version: 1,
      };
      
      setDocuments([newDoc, ...documents]);
      toast.success('Document uploaded successfully');
      setShowUploadModal(false);
      resetUploadForm();
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const getCategoryFromType = (type: DocumentType): string => {
    const categories: Record<DocumentType, string> = {
      general_liability: 'Insurance',
      workers_comp: 'Insurance',
      license: 'License',
      w9: 'Tax',
      certificate: 'Certificate',
      other: 'Other',
    };
    return categories[type] || 'Other';
  };

  const resetUploadForm = () => {
    setUploadForm({
      type: 'general_liability',
      name: '',
      file: null,
      expirationDate: '',
      policyNumber: '',
      provider: '',
      notes: '',
    });
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }
    
    try {
      // In production, delete from Firebase Storage and Firestore
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document deleted successfully');
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleDownloadDocument = async (doc: VendorDocument) => {
    try {
      // In production, download from Firebase Storage
      toast.success(`Downloading ${doc.name}...`);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handlePreviewDocument = (doc: VendorDocument) => {
    setSelectedDocument(doc);
    setShowPreviewModal(true);
  };

  const getFilteredDocuments = () => {
    let filtered = documents;
    
    if (filter === 'active') {
      filtered = documents.filter(d => d.status === 'active');
    } else if (filter === 'expiring') {
      filtered = documents.filter(d => d.status === 'expiring_soon');
    } else if (filter === 'expired') {
      filtered = documents.filter(d => d.status === 'expired');
    }
    
    return filtered.sort((a, b) => {
      // Sort by expiration date (soonest first)
      if (a.expirationDate && b.expirationDate) {
        return a.expirationDate.getTime() - b.expirationDate.getTime();
      }
      if (a.expirationDate) return -1;
      if (b.expirationDate) return 1;
      // Then by upload date (newest first)
      return b.uploadedAt.getTime() - a.uploadedAt.getTime();
    });
  };

  const stats = {
    total: documents.length,
    active: documents.filter(d => d.status === 'active').length,
    expiring: documents.filter(d => d.status === 'expiring_soon').length,
    expired: documents.filter(d => d.status === 'expired').length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Document Manager</h2>
          <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Insurance, licenses, and compliance documents for {vendor.displayName}</p>
        </div>
        
        <Button onClick={() => setShowUploadModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <Upload className="w-4 h-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`cursor-pointer hover:shadow-lg transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`} onClick={() => setFilter('all')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Documents</p>
                <p className={`text-3xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.total}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-lg transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`} onClick={() => setFilter('active')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Active</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{stats.active}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-lg transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`} onClick={() => setFilter('expiring')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Expiring Soon</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.expiring}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer hover:shadow-lg transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`} onClick={() => setFilter('expired')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Expired</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{stats.expired}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Expiration Alerts */}
      {(stats.expiring > 0 || stats.expired > 0) && (
        <Card className={`${isDarkMode ? 'border-yellow-700 bg-yellow-900/20' : 'border-yellow-200 bg-yellow-50'}`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mt-1" />
              <div className="flex-1">
                <h3 className={`font-semibold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-900'}`}>Action Required: Document Expiration</h3>
                <p className={`text-sm mt-1 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-700'}`}>
                  {stats.expired > 0 && `${stats.expired} document(s) have expired. `}
                  {stats.expiring > 0 && `${stats.expiring} document(s) will expire within 30 days. `}
                  Please upload updated versions to maintain compliance.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {(['all', 'active', 'expiring', 'expired'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
              filter === f
                ? 'bg-blue-600 text-white'
                : isDarkMode
                  ? 'bg-slate-800 text-gray-300 hover:bg-slate-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1).replace('_', ' ')}
            {f !== 'all' && ` (${stats[f]})`}
          </button>
        ))}
      </div>

      {/* Documents List */}
      {getFilteredDocuments().length === 0 ? (
        <Card className={isDarkMode ? 'bg-slate-900 border-slate-800' : ''}>
          <CardContent className="py-12 text-center">
            <FileText className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No documents found</h3>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {filter !== 'all' 
                ? `No ${filter} documents for this vendor`
                : 'Upload insurance certificates, licenses, and other compliance documents'}
            </p>
            <Button onClick={() => setShowUploadModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Upload className="w-4 h-4 mr-2" />
              Upload First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {getFilteredDocuments().map((doc) => (
            <Card key={doc.id} className={`hover:shadow-lg transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-800' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                      {doc.type === 'w9' ? (
                        <FileCheck className="w-6 h-6 text-blue-600" />
                      ) : doc.type.includes('liability') || doc.type === 'workers_comp' ? (
                        <Shield className="w-6 h-6 text-blue-600" />
                      ) : (
                        <FileText className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{doc.name}</h3>
                        {getStatusBadge(doc.status)}
                        <Badge variant="outline">{doc.category}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {doc.expirationDate && (
                          <div>
                            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Expires</p>
                            <p className={`font-medium flex items-center gap-1 ${isDarkMode ? 'text-gray-200' : ''}`}>
                              {getStatusIcon(doc.status)}
                              {doc.expirationDate.toLocaleDateString()}
                            </p>
                            {doc.status !== 'expired' && (
                              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                {getDaysUntilExpiration(doc.expirationDate)} days
                              </p>
                            )}
                          </div>
                        )}
                        
                        {doc.policyNumber && (
                          <div>
                            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Policy/License #</p>
                            <p className={`font-medium ${isDarkMode ? 'text-gray-200' : ''}`}>{doc.policyNumber}</p>
                          </div>
                        )}
                        
                        {doc.provider && (
                          <div>
                            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Provider</p>
                            <p className={`font-medium ${isDarkMode ? 'text-gray-200' : ''}`}>{doc.provider}</p>
                          </div>
                        )}
                        
                        <div>
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>File Size</p>
                          <p className={`font-medium ${isDarkMode ? 'text-gray-200' : ''}`}>{formatFileSize(doc.fileSize)}</p>
                        </div>
                      </div>
                      
                      <div className={`mt-3 flex items-center gap-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        <span>Uploaded by {doc.uploadedBy.name}</span>
                        <span>•</span>
                        <span>{doc.uploadedAt.toLocaleDateString()}</span>
                        {doc.version > 1 && (
                          <>
                            <span>•</span>
                            <span>Version {doc.version}</span>
                          </>
                        )}
                      </div>
                      
                      {doc.notes && (
                        <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{doc.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreviewDocument(doc)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadDocument(doc)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteDocument(doc.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-6 h-6" />
              Upload Document
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUploadDocument} className="space-y-6 mt-4">
            {/* Document Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Document Type *
              </label>
              <select
                value={uploadForm.type}
                onChange={(e) => setUploadForm({ ...uploadForm, type: e.target.value as DocumentType })}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'border-gray-300'}`}
                required
                aria-label="Document type"
              >
                <option value="general_liability">General Liability Insurance</option>
                <option value="workers_comp">Workers Compensation Insurance</option>
                <option value="license">License/Certification</option>
                <option value="w9">W-9 Tax Form</option>
                <option value="certificate">Certificate</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            {/* File Upload */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                File Upload * (PDF, JPG, PNG - Max 10MB)
              </label>
              <div className="flex items-center gap-4">
                <label className={`flex-1 flex items-center justify-center px-6 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isDarkMode ? 'border-slate-600 hover:border-blue-500 bg-slate-800/50' : 'border-gray-300 hover:border-blue-500'}`}>
                  <div className="text-center">
                    {uploadForm.file ? (
                      <>
                        <FileText className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{uploadForm.file.name}</p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatFileSize(uploadForm.file.size)}</p>
                      </>
                    ) : (
                      <>
                        <Upload className={`w-12 h-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Click to upload or drag and drop</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    required
                  />
                </label>
              </div>
            </div>
            
            {/* Document Name */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Document Name
              </label>
              <Input
                value={uploadForm.name}
                onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                placeholder="e.g., General Liability Certificate 2024"
              />
            </div>
            
            {/* Expiration Date */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Expiration Date (if applicable)
              </label>
              <Input
                type="date"
                value={uploadForm.expirationDate}
                onChange={(e) => setUploadForm({ ...uploadForm, expirationDate: e.target.value })}
              />
            </div>
            
            {/* Policy/License Number */}
            {(uploadForm.type.includes('liability') || uploadForm.type === 'workers_comp' || uploadForm.type === 'license') && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {uploadForm.type === 'license' ? 'License Number' : 'Policy Number'}
                </label>
                <Input
                  value={uploadForm.policyNumber}
                  onChange={(e) => setUploadForm({ ...uploadForm, policyNumber: e.target.value })}
                  placeholder="Enter policy or license number"
                />
              </div>
            )}
            
            {/* Provider */}
            {(uploadForm.type.includes('liability') || uploadForm.type === 'workers_comp') && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Insurance Provider
                </label>
                <Input
                  value={uploadForm.provider}
                  onChange={(e) => setUploadForm({ ...uploadForm, provider: e.target.value })}
                  placeholder="e.g., State Farm, Allstate"
                />
              </div>
            )}
            
            {/* Notes */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Notes (optional)
              </label>
              <textarea
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                rows={3}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' : 'border-gray-300'}`}
                placeholder="Add any additional notes about this document..."
              />
            </div>
            
            {/* Upload Progress */}
            {isUploading && (
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Uploading...</span>
                  <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{uploadProgress}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-200'}`}>
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Actions */}
            <div className={`flex justify-end gap-3 pt-4 border-t ${isDarkMode ? 'border-slate-700' : ''}`}>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUploadModal(false);
                  resetUploadForm();
                }}
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={isUploading || !uploadForm.file}
              >
                {isUploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      {selectedDocument && (
        <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-6 h-6" />
                {selectedDocument.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              {selectedDocument.mimeType === 'application/pdf' ? (
                <div className={`rounded-lg p-8 text-center ${isDarkMode ? 'bg-slate-800' : 'bg-gray-100'}`}>
                  <FileText className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>PDF Preview</p>
                  <Button onClick={() => handleDownloadDocument(selectedDocument)}>
                    <Download className="w-4 h-4 mr-2" />
                    Download to View
                  </Button>
                </div>
              ) : (
                <img
                  src={selectedDocument.fileUrl}
                  alt={selectedDocument.name}
                  className="w-full h-auto rounded-lg"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
